/**
 * Merge two accounts (duplicate repair — re-connecting one IBAN over a second
 * channel mints a second account, see `POST /accounts/{id}/merge`).
 *
 * Flow: pick a target → load a dry-run preview (moved/duplicates/enriched) →
 * confirm. The confirm button stays disabled until a preview for the current
 * selection has loaded, so the counts are always seen before anything writes.
 * Accounts with the same IBAN as the source are sorted first and marked — they
 * are the duplicates this exists for.
 *
 * The stateful body is mounted only while open, so state initialises fresh from
 * the current `source` prop each time (no reset effect).
 */
import { useMemo, useState } from 'react';
import { Modal } from '../../components/ui/Modal.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Field } from '../../components/ui/Field.tsx';
import { InlineError } from '../../components/ui/InlineError.tsx';
import { Select } from '../../components/ui/Select.tsx';
import { WarningList } from '../../components/ui/WarningList.tsx';
import { mergeReportLines } from './mergeReport.ts';
import { ApiRequestError } from '../../lib/api.ts';
import { accountChannelLabel } from '../../lib/labels.ts';
import { useAccounts } from '../../lib/hooks/useAccounts.ts';
import { useMergeAccounts } from '../../lib/hooks/useAccountMutations.ts';
import type { AccountMergeOut, AccountOut } from '../../lib/types.ts';

export interface MergeAccountsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The SOURCE — its history moves away and it gets closed afterwards. */
  source: AccountOut | null;
}

export function MergeAccountsDialog({ open, onOpenChange, source }: MergeAccountsDialogProps) {
  if (!open || !source) return null;
  return <MergeForm source={source} onClose={() => onOpenChange(false)} />;
}

function MergeForm({ source, onClose }: { source: AccountOut; onClose: () => void }) {
  const merge = useMergeAccounts();
  const accounts = useAccounts();
  const [targetId, setTargetId] = useState<number | null>(null);
  const [adoptIdentity, setAdoptIdentity] = useState(false);
  const [keepAsSource, setKeepAsSource] = useState(false);
  const [preview, setPreview] = useState<AccountMergeOut | null>(null);
  const [result, setResult] = useState<AccountMergeOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Multi-source: only a FinTS channel may stay attached as a feeder — a second
  // EB channel would fight the target's own pending projection (backend guard).
  const canKeepAsSource = source.connection_provider === 'fints';

  const targets = useMemo(() => {
    const others = accounts.filter((a) => a.id !== source.id);
    // Same-IBAN accounts first — those are the duplicate pairs.
    return [...others].sort((a, b) => {
      const aTwin = source.iban != null && a.iban === source.iban ? 0 : 1;
      const bTwin = source.iban != null && b.iban === source.iban ? 0 : 1;
      return aTwin - bTwin || a.sort_order - b.sort_order || a.id - b.id;
    });
  }, [accounts, source]);

  const target = targets.find((a) => a.id === targetId) ?? null;
  // A preview is only trustworthy for the exact selection it was requested for.
  const previewCurrent =
    preview !== null &&
    preview.target.id === targetId &&
    preview.report.adopted_identity === (adoptIdentity && !source.is_manual) &&
    preview.report.kept_as_source === (keepAsSource && canKeepAsSource);

  function describeError(err: unknown): string {
    return err instanceof ApiRequestError
      ? err.message
      : 'Zusammenführen fehlgeschlagen. Bitte erneut versuchen.';
  }

  function loadPreview() {
    if (targetId === null) return;
    setError(null);
    merge.mutate(
      {
        sourceId: source.id,
        body: {
          target_account_id: targetId,
          dry_run: true,
          adopt_identity: adoptIdentity,
          keep_as_source: keepAsSource && canKeepAsSource,
        },
      },
      {
        onSuccess: setPreview,
        onError: (err) => setError(describeError(err)),
      },
    );
  }

  function confirmMerge() {
    if (targetId === null) return;
    setError(null);
    merge.mutate(
      {
        sourceId: source.id,
        body: {
          target_account_id: targetId,
          adopt_identity: adoptIdentity,
          keep_as_source: keepAsSource && canKeepAsSource,
        },
      },
      {
        onSuccess: setResult,
        onError: (err) => setError(describeError(err)),
      },
    );
  }

  if (result) {
    return (
      <Modal
        open
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
        title="Konten zusammengeführt"
        description={`„${result.source.name}" → „${result.target.name}"`}
        size="md"
        footer={<Button onClick={onClose}>Schließen</Button>}
      >
        <div className="space-y-4">
          <ReportSummary report={result.report} />
          <WarningList warnings={result.report.warnings} />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Konten zusammenführen"
      description={`${source.name} (${accountChannelLabel(source)}, ${source.transaction_count} Buchungen)`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={merge.isPending}>
            Abbrechen
          </Button>
          {previewCurrent ? (
            <Button variant="danger" onClick={confirmMerge} disabled={merge.isPending}>
              Zusammenführen
            </Button>
          ) : (
            <Button onClick={loadPreview} disabled={targetId === null || merge.isPending}>
              Vorschau laden
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <InlineError message={error} />

        <p className="text-sm text-slate-600 dark:text-slate-300">
          Alle Buchungen dieses Kontos werden in das Zielkonto übernommen; Buchungen, die dort
          schon existieren, werden übersprungen.{' '}
          {keepAsSource && canKeepAsSource
            ? 'Das Quellkonto bleibt als zweite Datenquelle verbunden.'
            : 'Das Quellkonto wird anschließend geschlossen.'}
        </p>

        {targets.length === 0 ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            Es gibt kein zweites offenes Konto, in das dieses Konto übernommen werden könnte.
            Geschlossene Konten kommen als Ziel nicht in Frage — öffne das gewünschte Zielkonto
            zuerst wieder.
          </p>
        ) : null}

        <Field label="Zielkonto" required>
          {(id) => (
            <Select
              id={id}
              value={targetId === null ? undefined : String(targetId)}
              onValueChange={(value) => {
                setTargetId(Number(value));
                setPreview(null);
              }}
              options={targets.map((a) => ({
                value: String(a.id),
                label:
                  `${a.name} — ${accountChannelLabel(a)} (${a.transaction_count} Buchungen)` +
                  (source.iban != null && a.iban === source.iban ? ' · gleiche IBAN' : ''),
              }))}
            />
          )}
        </Field>

        {target !== null && source.iban != null && target.iban !== source.iban ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            Die IBANs unterscheiden sich — sicher, dass es dasselbe reale Konto ist?
          </p>
        ) : null}

        {canKeepAsSource ? (
          <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={keepAsSource}
              onChange={(e) => {
                setKeepAsSource(e.target.checked);
                if (e.target.checked) setAdoptIdentity(false);
                setPreview(null);
              }}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600"
            />
            <span>
              Als zweite Datenquelle behalten: „{accountChannelLabel(source)}" synchronisiert
              weiter und schreibt künftige Buchungen direkt in das Zielkonto — z.&nbsp;B. ergänzt
              FinTS die Händlernamen, die im PSD2-Zugang fehlen.
              <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                Beide Bankzugänge bleiben verbunden; Duplikate werden über den normalen
                Duplikat-Abgleich zusammengeführt.
              </span>
            </span>
          </label>
        ) : null}

        {!source.is_manual && !keepAsSource ? (
          <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={adoptIdentity}
              onChange={(e) => {
                setAdoptIdentity(e.target.checked);
                if (e.target.checked) setKeepAsSource(false);
                setPreview(null);
              }}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600"
            />
            <span>
              Bankzugang übernehmen: „{accountChannelLabel(source)}" synchronisiert künftig das
              Zielkonto.
              {target !== null && !target.is_manual ? (
                <span className="mt-1 block text-xs text-amber-700 dark:text-amber-400">
                  Der bisherige Zugang des Zielkontos ({accountChannelLabel(target)}) muss danach
                  unter Bankverbindungen getrennt werden, sonst entsteht wieder ein Duplikat.
                </span>
              ) : null}
            </span>
          </label>
        ) : null}

        {previewCurrent ? (
          <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/50">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Vorschau
            </p>
            <ReportSummary report={preview.report} />
            <WarningList warnings={preview.report.warnings} />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function ReportSummary({ report }: { report: AccountMergeOut['report'] }) {
  return (
    <ul className="list-inside list-disc space-y-0.5 text-sm text-slate-700 dark:text-slate-300">
      {mergeReportLines(report).map((part) => (
        <li key={part}>{part}</li>
      ))}
      {report.archived_source ? <li>Das Quellkonto wird geschlossen</li> : null}
      {report.adopted_identity ? <li>Der Bankzugang des Quellkontos zieht mit um</li> : null}
    </ul>
  );
}
